import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useSearch } from "wouter";
import { getArticleUrl } from "@/lib/article-url";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalLink, BookOpen, Building2, ChevronRight, Calendar, Globe, Home, ArrowLeft } from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import type { GlossaryEntry, Company, Campaign, CampaignPlacement, CampaignAsset, Article, InsightDisplayTemplate } from "@shared/schema";
import { marked } from "marked";

function getAssetImageUrl(asset: CampaignAsset): string {
  if (!asset.imageUrl) return "";
  if (asset.imageUrl.startsWith("/objects/") || asset.imageUrl.includes("/objects/uploads/")) {
    return `/api/campaign-assets/${asset.id}/image`;
  }
  return asset.imageUrl;
}

interface CampaignWithPlacements extends Campaign {
  placements: (CampaignPlacement & { asset: CampaignAsset })[];
}

function stripCodeFence(content: string): string {
  const trimmed = content.trim();
  const fenceMatch = trimmed.match(/^```[a-z]*\n([\s\S]*?)```\s*$/i);
  if (fenceMatch) return fenceMatch[1];
  return content;
}

function MarkdownContent({ content, className }: { content: string; className?: string }) {
  const stripped = stripCodeFence(content);
  const isHtml = stripped.trimStart().startsWith("<");
  const html = isHtml ? stripped : (marked.parse(stripped) as string);
  return (
    <div
      className={`prose prose-slate dark:prose-invert max-w-none ${className || ""}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default function InsightPage() {
  const { slug } = useParams<{ slug: string }>();
  const searchString = useSearch();
  const [wikiImageFailed, setWikiImageFailed] = useState(false);

  const sourceArticleSlug = useMemo(() => {
    const params = new URLSearchParams(searchString);
    return params.get("from");
  }, [searchString]);

  useEffect(() => {
    setWikiImageFailed(false);
  }, [slug]);

  const { data: insight, isLoading, isFetching } = useQuery<GlossaryEntry & { displayTemplate?: InsightDisplayTemplate }>({
    queryKey: ["/api/insights", slug],
    enabled: !!slug,
  });

  const template = insight?.displayTemplate;

  const { data: sourceArticle } = useQuery<Article>({
    queryKey: ["/api/articles/slug", sourceArticleSlug],
    enabled: !!sourceArticleSlug,
  });

  const showLoading = isLoading || isFetching || !slug;

  const { data: sponsor } = useQuery<Company>({
    queryKey: ["/api/companies", insight?.sponsorCompanyId],
    enabled: !!insight?.sponsorCompanyId,
  });

  const { data: campaignSponsorship } = useQuery<CampaignWithPlacements | null>({
    queryKey: ["/api/insights", slug, "sponsorship"],
    enabled: !!slug,
  });

  const headerPlacement = campaignSponsorship?.placements?.find(p => p.placementType === "deepdive_header");
  const headerAsset = headerPlacement?.asset;
  const sidebarPlacement = campaignSponsorship?.placements?.find(p => p.placementType === "deepdive_sidebar");
  const sidebarAsset = sidebarPlacement?.asset;

  useEffect(() => {
    if (!insight) return;
    const scriptId = `ld-defined-term-${insight.id}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    const pageUrl = `${window.location.origin}/insights/${insight.slug}`;
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      "name": insight.title || insight.term,
      "description": insight.definition || undefined,
      "url": pageUrl,
      "inDefinedTermSet": {
        "@type": "DefinedTermSet",
        "name": "Industry Glossary",
        "url": `${window.location.origin}/glossary`,
      },
    });
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [insight]);

  const keywordTypeColors: Record<string, string> = {
    technical_term: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    standard: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    product_brand: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    product_description: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
    acronym: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    concept: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200",
  };

  if (showLoading && !insight) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 max-w-7xl mx-auto px-4 py-6 lg:py-10 w-full">
          <Skeleton className="h-9 w-28 mb-6" />
          
          <div className="lg:flex lg:gap-10">
            <div className="lg:flex-1 min-w-0">
              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-16" />
                </div>
                <Skeleton className="h-10 md:h-12 w-4/5 mb-4" />
                <Skeleton className="h-6 w-full mb-4" />
                <div className="flex flex-wrap items-center gap-4 mt-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              
              <Skeleton className="h-48 w-full rounded-lg mb-8" />
              
              <div className="space-y-6">
                <div>
                  <Skeleton className="h-8 w-48 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
                <div>
                  <Skeleton className="h-8 w-56 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
                <div>
                  <Skeleton className="h-8 w-40 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            </div>
            
            <aside className="hidden lg:block lg:w-80 lg:flex-shrink-0 space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <Skeleton className="h-5 w-24" />
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <Skeleton className="h-40 w-full rounded-lg mb-2" />
                  <Skeleton className="h-3 w-32 mx-auto" />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <Skeleton className="h-5 w-28" />
                </CardHeader>
                <CardContent className="space-y-2">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!insight) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-4">Insight Not Found</h1>
          <p className="text-muted-foreground mb-6">This insight page doesn't exist or hasn't been published yet.</p>
          <Button asChild>
            <Link href="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <ScrollArea className="h-screen">
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 max-w-7xl mx-auto px-4 py-6 lg:py-10 w-full">
          <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6" data-testid="breadcrumb-nav">
            <Link href="/" className="flex items-center gap-1 hover:text-foreground transition-colors">
              <Home className="h-4 w-4" />
              <span>Home</span>
            </Link>
            {sourceArticle ? (
              <>
                <ChevronRight className="h-4 w-4" />
                <Link 
                  href={getArticleUrl(sourceArticle)} 
                  className="hover:text-foreground transition-colors max-w-[300px] truncate"
                  data-testid="breadcrumb-article"
                >
                  {sourceArticle.title}
                </Link>
                <ChevronRight className="h-4 w-4" />
                <span className="text-foreground font-medium" data-testid="breadcrumb-insight">
                  {insight?.term || "Insight"}
                </span>
              </>
            ) : (
              <>
                <ChevronRight className="h-4 w-4" />
                <span className="text-foreground font-medium" data-testid="breadcrumb-insight">
                  {insight?.term || "Insight"}
                </span>
              </>
            )}
          </nav>

          <div className="lg:flex lg:gap-10">
            <article className="lg:flex-1 min-w-0">
              <header className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {insight.keywordType && (
                    <Badge className={keywordTypeColors[insight.keywordType] || "bg-gray-100"}>
                      {insight.keywordType.replace("_", " ")}
                    </Badge>
                  )}
                  {insight.categoryTags?.map((tag) => (
                    <Badge key={tag} variant="outline">{tag}</Badge>
                  ))}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-insight-title">
                  {insight.title || insight.term}
                </h1>
                {insight.definition && (() => {
                  const titleWithoutParens = (insight.title || insight.term || "").replace(/\s*\([^)]*\)\s*/g, "").toLowerCase().trim();
                  const def = insight.definition.toLowerCase().trim();
                  return def !== titleWithoutParens;
                })() && (
                  <p className="text-xl text-muted-foreground" data-testid="text-insight-definition">
                    {insight.definition}
                  </p>
                )}
                
                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
                  {insight.readTimeMinutes && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {insight.readTimeMinutes} min read
                    </span>
                  )}
                  {insight.insightPublishedAt && (
                    <span>Published {new Date(insight.insightPublishedAt).toLocaleDateString()}</span>
                  )}
                </div>
              </header>

              {headerAsset && (
                <div className="mb-8 rounded-lg overflow-hidden border bg-gradient-to-r from-muted/30 to-muted/10" data-testid="sponsor-header-banner">
                  {headerAsset.imageUrl && (
                    <a 
                      href={headerPlacement?.ctaUrl || headerAsset.defaultCtaUrl || "#"} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={getAssetImageUrl(headerAsset)} 
                        alt={headerAsset.name}
                        className="w-full h-auto object-cover"
                        data-testid="sponsor-header-image"
                      />
                    </a>
                  )}
                  <div className="p-4">
                    {headerAsset.overlayText && (
                      <p className="text-sm md:text-base text-muted-foreground mb-3" data-testid="sponsor-header-text">
                        {headerAsset.overlayText}
                      </p>
                    )}
                    {(headerPlacement?.ctaUrl || headerAsset.defaultCtaUrl) && (
                      <Button asChild size="sm" variant="outline" data-testid="sponsor-header-cta">
                        <a 
                          href={headerPlacement?.ctaUrl || headerAsset.defaultCtaUrl || "#"} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {headerPlacement?.ctaLabel || headerAsset.defaultCtaLabel || "Learn More"}
                          <ExternalLink className="ml-2 h-3 w-3" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {insight.insightSummary && (
                <div className="mb-8 p-4 bg-muted/50 rounded-lg border-l-4 border-primary">
                  <p className="text-lg text-foreground/90 leading-relaxed" data-testid="text-insight-summary">
                    {insight.insightSummary}
                  </p>
                </div>
              )}

              {insight.insightContent && (
                <section className="mb-8" data-testid="section-insight-content">
                  <MarkdownContent content={insight.insightContent} />
                </section>
              )}

              {insight.whyItMatters && (
                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle className="text-lg">Why It Matters</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MarkdownContent content={insight.whyItMatters} />
                  </CardContent>
                </Card>
              )}

              {insight.inPractice && (
                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle className="text-lg">In Practice</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MarkdownContent content={insight.inPractice} />
                  </CardContent>
                </Card>
              )}

              {insight.commonPitfalls && (
                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle className="text-lg">Common Pitfalls</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MarkdownContent content={insight.commonPitfalls} />
                  </CardContent>
                </Card>
              )}

              {insight.wikipediaExtract && (template?.showWikipedia !== false) && (
                <Card className="mb-8 border-blue-200 dark:border-blue-800">
                  <CardHeader className="bg-blue-50 dark:bg-blue-950/30">
                    <div className="flex items-center justify-between gap-4">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Globe className="h-5 w-5" />
                        Wikipedia
                      </CardTitle>
                      {insight.wikipediaUrl && (
                        <a
                          href={insight.wikipediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                          data-testid="link-wikipedia"
                        >
                          Read full article
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <p className="text-foreground/80 leading-relaxed whitespace-pre-line">
                      {insight.wikipediaExtract.length > 1500 
                        ? insight.wikipediaExtract.slice(0, 1500) + "..."
                        : insight.wikipediaExtract
                      }
                    </p>
                    <p className="text-xs text-muted-foreground mt-4">
                      Content from Wikipedia, licensed under CC BY-SA 3.0
                    </p>
                  </CardContent>
                </Card>
              )}

              {insight.relatedTerms && insight.relatedTerms.length > 0 && (
                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle className="text-lg">Related Terms</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {insight.relatedTerms.map((term) => (
                        <Badge key={term} variant="secondary" className="cursor-pointer hover-elevate">
                          {term}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </article>

            <aside className="hidden lg:block lg:w-80 space-y-6">
              {sidebarAsset && (template?.showSponsor !== false) && (
                <Card className="border-primary/20 bg-primary/5 sticky top-6" data-testid="sponsor-sidebar-card">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex flex-col items-center text-center">
                      {sidebarAsset.imageUrl && (
                        <div className="w-full mb-3">
                          <img 
                            src={getAssetImageUrl(sidebarAsset)} 
                            alt={sidebarAsset.name} 
                            className="w-full h-auto object-contain rounded"
                            data-testid="sponsor-sidebar-image"
                          />
                        </div>
                      )}
                      {sidebarAsset.overlayText && (
                        <p className="text-sm text-muted-foreground mb-3" data-testid="sponsor-sidebar-text">
                          {sidebarAsset.overlayText}
                        </p>
                      )}
                      {(sidebarPlacement?.ctaUrl || sidebarAsset.defaultCtaUrl) && (
                        <Button asChild size="sm" className="w-full" data-testid="sponsor-sidebar-cta">
                          <a 
                            href={sidebarPlacement?.ctaUrl || sidebarAsset.defaultCtaUrl || "#"} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            {sidebarPlacement?.ctaLabel || sidebarAsset.defaultCtaLabel || "Learn More"}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {sponsor && !sidebarAsset && (template?.showSponsor !== false) && (
                <Card className="border-primary/20 bg-primary/5 sticky top-6">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-16 h-16 bg-background rounded-lg flex items-center justify-center mb-3">
                        {sponsor.logo ? (
                          <img src={sponsor.logo} alt={sponsor.name} className="max-w-full max-h-full object-contain rounded" />
                        ) : (
                          <Building2 className="h-8 w-8 text-primary" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">Sponsored by</p>
                      <p className="font-semibold">{sponsor.name}</p>
                      {insight.sponsorTagline && (
                        <p className="text-sm text-muted-foreground mt-2">{insight.sponsorTagline}</p>
                      )}
                      {insight.sponsorCtaUrl && (
                        <Button asChild size="sm" className="mt-4 w-full">
                          <a href={insight.sponsorCtaUrl} target="_blank" rel="noopener noreferrer" data-testid="button-sponsor-cta">
                            {insight.sponsorCtaLabel || "Learn More"}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {insight.wikipediaImage && !wikiImageFailed && (
                <Card data-testid="wikipedia-image-card">
                  <CardContent className="p-4">
                    <img 
                      src={insight.wikipediaImage} 
                      alt={insight.term}
                      className="w-full rounded-lg"
                      onError={() => setWikiImageFailed(true)}
                      data-testid="wikipedia-image"
                    />
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Image from Wikipedia
                    </p>
                  </CardContent>
                </Card>
              )}
            </aside>
          </div>
        </div>
        <SiteFooter />
      </div>
    </ScrollArea>
  );
}
